import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';

import { useIsMobileLayout } from 'hooks/useMediaQuery';
import { prepareImageFile } from 'utils/prepareImageFile';
import { validateAvatarFile } from 'utils/validateAvatarImage';

import { AvatarCropModal } from './AvatarCropModal';
import { CameraCaptureModal } from './CameraCaptureModal';
import styles from './PhotoUploadField.module.scss';

import { Field, getIn, type FieldProps } from 'formik';

interface PhotoUploadFieldProps {
  name: string;
  placeholderImage?: string;
  width?: number;
  height?: number;
  status?: string;
  /** Profile photo: interactive 3:4 crop + avatar validation */
  avatarRules?: boolean;
}

const GALLERY_ACCEPT = {
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
  'image/heic': ['.heic'],
  'image/heif': ['.heif'],
} as const;

const resolveExistingPhotoSrc = (value: string): string => {
  if (value.startsWith('blob:') || value.startsWith('http') || value.startsWith('/')) {
    return value;
  }
  return `/v1/get_image/${value}`;
};

const PhotoUploadPreview: React.FC<{
  value: unknown;
  placeholderImage?: string;
  width: number;
  height: number;
  avatarRules: boolean;
  status?: string;
  error?: string;
  touched?: boolean;
  onPick: (file: File) => void;
  onRejectMessage: (message: string) => void;
}> = ({
  value,
  placeholderImage,
  width,
  height,
  avatarRules,
  status,
  error,
  touched,
  onPick,
  onRejectMessage,
}) => {
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropFileName, setCropFileName] = useState('avatar.jpg');
  const [cameraOpen, setCameraOpen] = useState(false);
  const [captureFacing, setCaptureFacing] = useState<'user' | 'environment'>('environment');
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobileLayout();
  const cameraVariant = isMobile ? 'mobile' : 'desktop';

  useEffect(() => {
    if (!(value instanceof File)) {
      setFilePreviewUrl(null);
      return;
    }

    const url = URL.createObjectURL(value);
    setFilePreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [value]);

  useEffect(() => {
    return () => {
      if (cropSrc) URL.revokeObjectURL(cropSrc);
    };
  }, [cropSrc]);

  const finalizeFile = async (file: File, alreadyCropped: boolean) => {
    setBusy(true);
    try {
      const prepared = await prepareImageFile(file, {
        normalizeAvatar: avatarRules && !alreadyCropped,
      });
      if (avatarRules) {
        const validationError = await validateAvatarFile(prepared);
        if (validationError) {
          onRejectMessage(validationError);
          return;
        }
      }
      onPick(prepared);
    } catch (err) {
      onRejectMessage(err instanceof Error ? err.message : 'Не удалось обработать фото');
    } finally {
      setBusy(false);
    }
  };

  const openCropForFile = async (file: File) => {
    setBusy(true);
    try {
      // HEIC - JPEG before crop UI (cropper needs a paintable bitmap)
      const prepared = await prepareImageFile(file, { normalizeAvatar: false });
      if (cropSrc) URL.revokeObjectURL(cropSrc);
      const url = URL.createObjectURL(prepared);
      setCropFileName(prepared.name || file.name || 'avatar.jpg');
      setCropSrc(url);
    } catch (err) {
      onRejectMessage(err instanceof Error ? err.message : 'Не удалось обработать фото');
    } finally {
      setBusy(false);
    }
  };

  const handleFiles = async (accepted: File[], rejected?: FileRejection[]) => {
    if (rejected?.length) {
      onRejectMessage('Допустимы JPEG, PNG или HEIC (iPhone)');
      return;
    }
    if (!accepted.length) return;
    if (avatarRules) {
      await openCropForFile(accepted[0]);
      return;
    }
    await finalizeFile(accepted[0], false);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted, rejected) => {
      void handleFiles(accepted, rejected);
    },
    accept: GALLERY_ACCEPT,
    maxFiles: 1,
    multiple: false,
    noClick: true,
    disabled: busy,
  });
  const rootProps = getRootProps();

  const statuses: Record<string, string> = {
    pending_review: 'На модерации',
    approved: 'Подтверждено',
    rejected: 'Отклонено',
    moderation: 'На модерации',
    draft: 'Черновик',
  };

  const displayImage =
    value instanceof File
      ? filePreviewUrl || placeholderImage
      : typeof value === 'string' && value
        ? resolveExistingPhotoSrc(value)
        : placeholderImage;

  const displayStatus = value instanceof File ? 'pending_review' : status;
  const statusLabel = displayStatus ? statuses[displayStatus] || displayStatus : '';

  const onNativeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) void handleFiles([file]);
  };

  const openPhotoFlow = () => {
    // Mobile: live camera immediately - filters - crop.
    // Desktop: chooser first (file preferred, webcam optional).
    setCameraOpen(true);
  };

  const openGallery = () => {
    setCameraOpen(false);
    window.setTimeout(() => galleryInputRef.current?.click(), 50);
  };

  return (
    <div className={styles.photoUploadField}>
      {statusLabel && (
        <div className={styles.statusBadge} data-status={displayStatus}>
          {statusLabel}
        </div>
      )}

      <div
        {...rootProps}
        className={`${styles.photoArea} ${avatarRules ? styles.photoAreaAvatar : ''} ${
          isDragActive ? styles.dragActive : ''
        }`}
        style={{ width, height }}
        aria-label={isMobile ? 'Сделать фото' : 'Добавить фото'}
        onClick={(event) => {
          rootProps.onClick?.(event);
          if (!busy) openPhotoFlow();
        }}
      >
        <input {...getInputProps()} />

        <img
          src={displayImage}
          alt='Фото'
          className={styles.photoPreview}
          style={
            avatarRules
              ? { objectFit: 'contain', objectPosition: 'center' }
              : undefined
          }
        />

        {isDragActive && (
          <div className={styles.dragOverlay}>Отпустите чтобы загрузить фото</div>
        )}
        {busy && <div className={styles.dragOverlay}>Обработка...</div>}
      </div>

      <div className={styles.actionRow}>
        <button
          type='button'
          className={styles.uploadButton}
          disabled={busy}
          onClick={openPhotoFlow}
        >
          {isMobile ? 'Сделать фото' : 'Добавить фото'}
        </button>
        {isMobile ? (
          <button
            type='button'
            className={styles.uploadButtonSecondary}
            disabled={busy}
            onClick={openGallery}
          >
            Выбрать файл
          </button>
        ) : null}
      </div>

      <input
        ref={cameraInputRef}
        type='file'
        accept='image/jpeg,image/png,.jpg,.jpeg,.png'
        capture={captureFacing}
        className={styles.hiddenInput}
        onChange={onNativeChange}
      />
      <input
        ref={galleryInputRef}
        type='file'
        accept='image/jpeg,image/png,image/heic,image/heif,.jpg,.jpeg,.png,.heic,.heif'
        className={styles.hiddenInput}
        onChange={onNativeChange}
      />

      <p className={styles.formatHint}>
        JPEG или PNG
        {avatarRules ? ', пропорции 3:4 - выберите область на следующем шаге' : ''}. До 200 МБ (лимит сервера).
        {isMobile
          ? ' Можно перетащить файл на фото.'
          : ' На компьютере можно выбрать файл, перетащить его на фото или включить веб-камеру.'}
      </p>
      {touched && error && (
        <div className='form-error-message' style={{ color: 'red', marginTop: 8 }}>
          {String(error)}
        </div>
      )}

      {cameraOpen ? (
        <CameraCaptureModal
          variant={cameraVariant}
          onCancel={() => setCameraOpen(false)}
          onPickFile={openGallery}
          onCapture={(file) => {
            setCameraOpen(false);
            if (avatarRules) {
              void openCropForFile(file);
            } else {
              void finalizeFile(file, false);
            }
          }}
          onFallback={(facing) => {
            setCameraOpen(false);
            setCaptureFacing(facing);
            window.setTimeout(() => cameraInputRef.current?.click(), 50);
          }}
        />
      ) : null}

      {cropSrc ? (
        <AvatarCropModal
          imageSrc={cropSrc}
          fileName={cropFileName}
          aspect={3 / 4}
          onCancel={() => {
            URL.revokeObjectURL(cropSrc);
            setCropSrc(null);
          }}
          onComplete={(file) => {
            URL.revokeObjectURL(cropSrc);
            setCropSrc(null);
            void finalizeFile(file, true);
          }}
        />
      ) : null}
    </div>
  );
};

export const PhotoUploadField: React.FC<PhotoUploadFieldProps> = ({
  name,
  placeholderImage,
  status,
  width = 250,
  height = 300,
  avatarRules = false,
}) => {
  return (
    <Field name={name}>
      {({ field, form }: FieldProps) => {
        const error = getIn(form.errors, name);
        const touched = getIn(form.touched, name);

        return (
          <PhotoUploadPreview
            value={field.value}
            placeholderImage={placeholderImage}
            width={width}
            height={height}
            avatarRules={avatarRules}
            status={status}
            error={error}
            touched={touched}
            onPick={(file) => {
              form.setFieldError(name, undefined);
              form.setFieldValue(name, file);
              form.setFieldTouched(name, true, false);
            }}
            onRejectMessage={(message) => {
              form.setFieldError(name, message);
              form.setFieldTouched(name, true, false);
            }}
          />
        );
      }}
    </Field>
  );
};
